import { Injectable } from '@angular/core';
import * as Cesium from 'cesium';

type ListenerFunction = (...args: any[]) => void;

@Injectable({
  providedIn: 'root',
})
export class CoresService {
  private cesiumViewer: any;
  private leafletViewer: any;
  private globeClickListeners: Record<string, ListenerFunction> = {};
  private onTickListeners: Record<string, ListenerFunction> = {};
  private timeupdateListeners: Record<string, ListenerFunction> = {};
  private morphCompleteListeners: Record<string, ListenerFunction> = {};
  private morphStartListeners: Record<string, ListenerFunction> = {};
  private selectedEntity: any;

  constructor() {
  }

  // Timeline methods
  setCurrentTimestamp(timeInMilliseconds: number): void {
    if (this.cesiumViewer) {
      this.cesiumViewer.clock.currentTime = Cesium.JulianDate.fromDate(new Date(timeInMilliseconds));
      this.fireOnUpdateTime(this.cesiumViewer.clock.currentTime);
    }
  }
  getCurrentTime(): number | undefined {
    return this.cesiumViewer ? Cesium.JulianDate.toDate(this.cesiumViewer.clock.currentTime).getTime() : undefined;
  }
  getCurrentTimestamp(): any | undefined {
    return this.cesiumViewer ? Cesium.JulianDate.toDate(this.cesiumViewer.clock.currentTime) : undefined;
  }
  getCurrentJulianTime(): any | undefined {
    return this.cesiumViewer?.clock.currentTime;
  }
  pauseTime(): void {
    if (this.cesiumViewer) this.cesiumViewer.clock.shouldAnimate = false;
  }
  startTime(): void {
    if (this.cesiumViewer) this.cesiumViewer.clock.shouldAnimate = true;
  }

  // Globe event firing
  fireGlobeClickEventHandlers(lat: number, lon: number, height: number): void {
    Object.values(this.globeClickListeners).forEach((listener) => {
      (listener as ListenerFunction)(lat, lon, height);
    });
  }
  fireOnTickEventHandlers(clock: any, currentTime: any): void {
    Object.values(this.onTickListeners).forEach((listener) => {
      (listener as ListenerFunction)(clock, currentTime);
    });
  }
  fireOnUpdateTime(currentTime: any): void {
    Object.values(this.timeupdateListeners).forEach((listener) => {
      (listener as ListenerFunction)(currentTime);
    });
  }
  fireMorphStartListeners(prevMode: string, newMode: string): void {
    Object.values(this.morphStartListeners).forEach((listener) => {
      (listener as ListenerFunction)(prevMode, newMode);
    });
  }
  fireMorphCompleteListeners(prevMode: string, newMode: string): void {
    Object.values(this.morphCompleteListeners).forEach((listener) => {
      (listener as ListenerFunction)(prevMode, newMode);
    });
  }

  // Viewer setters/getters
  setViewer(viewer: any): void {
    this.cesiumViewer = viewer;
  }
  getViewer(): any {
    return this.cesiumViewer;
  }
  setLeafletViewer(viewer: any): void {
    this.leafletViewer = viewer;
  }
  getLeafletViewer(): any {
    return this.leafletViewer;
  }
  getScene(): any {
    return this.cesiumViewer?.scene;
  }

  // Listener registration/removal
  addGlobeClickListener(key: string, listener: ListenerFunction): void {
    this.globeClickListeners[key] = listener;
  }
  removeGlobeClickListener(key: string): void {
    delete this.globeClickListeners[key];
  }
  addOnTickListener(key: string, listener: ListenerFunction): void {
    this.onTickListeners[key] = listener;
  }
  removeOnTickListener(key: string): void {
    delete this.onTickListeners[key];
  }
  addTimeUpdateListener(key: string, listener: ListenerFunction): void {
    this.timeupdateListeners[key] = listener;
  }
  removeTimeUpdateListener(key: string): void {
    delete this.timeupdateListeners[key];
  }
  addMorphStartListener(key: string, listener: ListenerFunction): void {
    this.morphStartListeners[key] = listener;
  }
  removeMorphStartListener(key: string): void {
    delete this.morphStartListeners[key];
  }
  addMorphCompleteListener(key: string, listener: ListenerFunction): void {
    this.morphCompleteListeners[key] = listener;
  }
  removeMorphCompleteListener(key: string): void {
    delete this.morphCompleteListeners[key];
  }

  // Entity selection
  getSelectedEntity(): any {
    return this.selectedEntity;
  }
  setSelectedEntity(entity: any): void {
    if (this.selectedEntity && this.selectedEntity.setIsTargeted) {
      this.selectedEntity.setIsTargeted(false);
    }
    this.selectedEntity = entity;
  }
  clearSelectedEntity(): void {
    this.selectedEntity = undefined;
  }

  // Tracking entity
  getTrackingEntity(): any {
    return this.cesiumViewer ? this.cesiumViewer.trackedEntity : undefined;
  }
  setTrackingEntity(arcLiveEntity: any): void {
    if (arcLiveEntity && this.cesiumViewer) {
      this.cesiumViewer.trackedEntity = arcLiveEntity.getCesiumEntity();
    }
  }
  clearTrackingEntity(): void {
    if (this.cesiumViewer) {
      this.cesiumViewer.trackedEntity = undefined;
    }
  }

  // Scene mode
  setSceneModeTo2d(duration?: number): void {
    if (this.cesiumViewer) {
      this.cesiumViewer.scene.morphTo2D(duration || 0);
    }
  }
  setSceneModeTo3d(duration?: number): void {
    if (this.cesiumViewer) {
      this.cesiumViewer.scene.morphTo3D(duration || 0);
    }
  }
  getSceneMode(): any {
    return this.cesiumViewer?.scene.mode;
  }
  isIn2DMode(): boolean {
    return this.cesiumViewer?.scene.mode === Cesium.SceneMode.SCENE2D;
  }
  isIn3DMode(): boolean {
    return this.cesiumViewer?.scene.mode === Cesium.SceneMode.SCENE3D;
  }

  // Center coordinate
  getCenterCoordinate(): { lat: number; long: number } {
    if (!this.cesiumViewer) return { lat: 0, long: 0 };
    let pickPositionCartographic;
    if (this.cesiumViewer.scene.mode === Cesium.SceneMode.SCENE2D) {
      pickPositionCartographic = this.cesiumViewer.camera.positionCartographic;
      if (!pickPositionCartographic) return { lat: 0, long: 0 };
    } else if (this.cesiumViewer.scene.mode === Cesium.SceneMode.SCENE3D) {
      const windowPosition = new Cesium.Cartesian2(
        this.cesiumViewer.container.clientWidth / 2,
        this.cesiumViewer.container.clientHeight / 2
      );
      const pickRay = this.cesiumViewer.scene.camera.getPickRay(windowPosition);
      const pickPosition = this.cesiumViewer.scene.globe.pick(pickRay, this.cesiumViewer.scene);
      if (pickPosition) {
        pickPositionCartographic = this.cesiumViewer.scene.globe.ellipsoid.cartesianToCartographic(pickPosition);
      } else {
        return { lat: 0, long: 0 };
      }
    } else {
      return { lat: 0, long: 0 };
    }
    return {
      lat: pickPositionCartographic.latitude * (180 / Math.PI),
      long: pickPositionCartographic.longitude * (180 / Math.PI),
    };
  }
}
